import { ProgressSpinner } from 'primereact/progressspinner';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import './Splash.css';
import { AuthContext } from '../../context/AuthContext';
import { AuthService } from '../../services/auth.service';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { useRef } from 'react';
import axios from 'axios';

const SplashScreen = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { i18n } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingMaintenance, setIsCheckingMaintenance] = useState(true);
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState('');
  const [languageISO2, setLanguageISO2] = useState<string | undefined>(
    undefined,
  );
  const [showRecoverModal, setShowRecoverModal] = useState(false);
  const [recoverEmail, setRecoverEmail] = useState('');
  const [recoverError, setRecoverError] = useState<string | null>(null);
  const [recoverLoading, setRecoverLoading] = useState(false);

  const toast = useRef<Toast>(null);

  const authContext = useContext(AuthContext);

  useEffect(() => {
    const checkMaintenance = async () => {
      try {
        const baseUrl = process.env.REACT_APP_API_URL;
        const response = await axios.get(
          `${baseUrl}/AppConfig/maintenance/active`,
        );
        if (response.data && response.data.isEnabled) {
          setMaintenanceMode(true);
          setMaintenanceMessage(response.data.message);
          authContext?.logout();
        }
      } catch (err) {
        console.error('Error checking maintenance:', err);
      } finally {
        setIsCheckingMaintenance(false);
      }
    };
    checkMaintenance();
  }, []);

  const handleRecoverPassword = async () => {
    setRecoverLoading(true);
    setRecoverError(null);
    if (
      !recoverEmail.match(/^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/)
    ) {
      setRecoverError('Email inválido');
      setRecoverLoading(false);
      return;
    }
    try {
      await AuthService.PasswordReset(recoverEmail);
      setShowRecoverModal(false);
      setRecoverEmail('');
      toast.current?.show({
        severity: 'success',
        summary: t('splash.toasts.recovery_password.summary'),
        detail: '',
        life: 4000,
      });
    } catch (error) {
      setRecoverError('Error al enviar el correo');
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'No se pudo enviar el correo de recuperación.',
        life: 4000,
      });
    } finally {
      setRecoverLoading(false);
    }
  };

  const handleLogin = async () => {
    setIsLoading(true);
    setError(null);

    try {
      await authContext?.login(username, password, languageISO2);

      const role = AuthService.GetRole();
      console.log(role);

      if (role === 'KAM' || role === 'Delegate' || role === 'Congress') {
        navigate('/choiceCustomer');
      } else {
        navigate('/all-products');
      }
    } catch (err) {
      const error: Error = err as Error;
      console.log(error.message);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (authContext?.isAuthenticated && authContext?.isValidated) {
      const role = AuthService.GetRole();
      console.log('ROLE' + role);

      if (role === 'KAM' || role === 'Delegate' || role === 'Congress') {
        navigate('/choiceCustomer');
      } else {
        navigate('/all-products');
      }
      setIsLoading(false);
    }
  }, [authContext?.isAuthenticated, navigate]);

  useEffect(() => {
    setLanguageISO2(i18n.language.substring(0, 2));
  }, [i18n]);

  if (isCheckingMaintenance) {
    return (
      <div className="background">
        <img
          alt="background"
          className="long-logo"
          src="/images/logo_mesoestetic.png"
        />
        <img alt="name-logo" className="name-logo" src="/images/merchan.png" />
        <div className="form-container">
          <ProgressSpinner />
        </div>
      </div>
    );
  }

  if (maintenanceMode) {
    return (
      <div className="background">
        <img
          alt="background"
          className="long-logo"
          src="/images/logo_mesoestetic.png"
        />
        <img
          alt="name-logo"
          className="name-logo"
          src="/images/merchandising.png"
        />
        <div className="form-container">
          <p
            style={{
              textAlign: 'center',
              whiteSpace: 'pre-line',
              fontSize: '28px',
              fontWeight: 'bold',
            }}
          >
            {t('maintenance.message')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Dialog
        visible={showRecoverModal}
        header={t('splash.recovery_password')}
        style={{ width: '350px' }}
        modal
        footer={() => (
          <div
            style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}
          >
            <Button
              label={t('splash.cancel_button')}
              icon="pi pi-times"
              severity="secondary"
              className="cancel-recovery-button"
              onClick={() => {
                setShowRecoverModal(false);
                setRecoverEmail('');
                setRecoverError(null);
              }}
              outlined
            />
            <Button
              label={t('splash.send_button')}
              icon="pi pi-send"
              className="send-recovery-button"
              onClick={handleRecoverPassword}
              loading={recoverLoading}
            />
          </div>
        )}
        onHide={() => setShowRecoverModal(false)}
      >
        <div className="p-fluid">
          <label htmlFor="recoverEmail" style={{ fontWeight: 600 }}>
            {t('splash.email')}
          </label>
          <input
            id="recoverEmail"
            type="email"
            value={recoverEmail}
            className="input-field"
            onChange={(e) => setRecoverEmail(e.target.value)}
            placeholder={t('splash.email_placeholder')}
            autoFocus
          />
          {recoverError && (
            <div style={{ color: 'red', marginTop: 8 }}>{recoverError}</div>
          )}
        </div>
      </Dialog>
      <div className="background">
        <Toast ref={toast} />
        <img
          alt="background"
          className="long-logo"
          src="/images/logo_mesoestetic.png"
        />
        <img
          alt="name-logo"
          className="name-logo"
          src="/images/merchandising.png"
        />
        <div className="form-container">
          {isLoading ? (
            <div className="card flex justify-content-center">
              <ProgressSpinner />
            </div>
          ) : (
            <>
              {error && <div className="error">{error}</div>}
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleLogin();
                }}
                autoComplete="off"
                className="form-container"
              >
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder={t('splash.username')}
                  className="input-field"
                />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('splash.password')}
                  className="input-field"
                />

                <button type="submit" className="microsoft-button">
                  {t('splash.login')}
                </button>
              </form>
              <Button
                link
                severity="secondary"
                className="recovery-password"
                onClick={() => setShowRecoverModal(true)}
              >
                {t('splash.recovery_password')}
              </Button>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default SplashScreen;
